import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";

const sections = [
  {
    num: "1",
    title: "Introduction",
    content:
      "Presuni Ltd is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.",
  },
  {
    num: "2",
    title: "Information We Collect",
    items: [
      {
        sub: "Information You Provide",
        bullets: ["Name", "Email address", "Phone number", "Postal address", "Any information submitted through contact forms or communications"],
      },
      {
        sub: "Automatically Collected Information",
        bullets: ["IP address", "Browser type and version", "Pages visited and time spent on our site", "Device information"],
      },
      {
        sub: "Cookies and Tracking Technologies",
        bullets: ["We may use cookies and similar technologies to enhance user experience and analyse website traffic."],
      },
    ],
  },
  {
    num: "3",
    title: "How We Use Your Information",
    bullets: [
      "To provide and maintain our services",
      "To respond to inquiries or customer service requests",
      "To improve our website and user experience",
      "To send updates, marketing communications (where permitted)",
      "To comply with legal obligations",
    ],
  },
  {
    num: "4",
    title: "Legal Basis for Processing",
    bullets: [
      "Your consent",
      "Performance of a contract",
      "Compliance with legal obligations",
      "Legitimate business interests",
    ],
  },
  {
    num: "5",
    title: "Sharing Your Information",
    content:
      "Presuni Ltd does not sell your personal data. We may share data with trusted third-party service providers (e.g., hosting, analytics, email delivery) under strict confidentiality agreements and only where necessary to operate our services.",
  },
  {
    num: "6",
    title: "Data Retention",
    content:
      "We retain personal data for as long as necessary to fulfil the purposes outlined in this policy, or as required by law. Typically, contact form submissions are retained for 24 months unless you request deletion earlier.",
  },
  {
    num: "7",
    title: "Your Rights",
    bullets: [
      "Right to access your personal data",
      "Right to rectify inaccurate or incomplete data",
      "Right to erasure (right to be forgotten)",
      "Right to restrict processing",
      "Right to data portability",
      "Right to object to processing",
    ],
  },
  {
    num: "8",
    title: "Security",
    content:
      "We implement industry-standard security measures including encryption, access controls, and regular audits to protect your data. However, no system is entirely immune to risk, and we encourage users to use strong passwords and secure channels.",
  },
  {
    num: "9",
    title: "Contact Information",
    bullets: [
      "10 Winrush Close, Dudley, DY3 2NE, United Kingdom",
      "Email: info@presuni.co.uk",
    ],
  },
];

export default function Privacy() {
  return (
    <PageShell>
      <section className="w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-2">
                Privacy Policy
              </h1>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">
                Presuni Ltd
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                Company Number: 05745823
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-8">
                Registered Address: 10 Winrush Close, DY3 2NE, Dudley, West Midlands, United Kingdom
              </p>

              <div className="space-y-8">
                {sections.map((s) => (
                  <div
                    key={s.num}
                    className="p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border"
                  >
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                      {s.num}. {s.title}
                    </h2>
                    {s.content && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        {s.content}
                      </p>
                    )}
                    {s.bullets && (
                      <ul className="space-y-2">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                            <i className="ri-checkbox-blank-circle-fill text-brand-500 text-[6px] mt-2 flex-shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.items && (
                      <div className="space-y-4">
                        {s.items.map((item) => (
                          <div key={item.sub}>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              {item.sub}
                            </p>
                            <ul className="space-y-2">
                              {item.bullets.map((b) => (
                                <li key={b} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                                  <i className="ri-checkbox-blank-circle-fill text-brand-500 text-[6px] mt-2 flex-shrink-0" />
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 mt-8">
                Last updated: 30/04/2026
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageShell>
  );
}
